import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { fetchKYCStatus } from '../../store/slices/kycSlice';
import KYCStatusBadge from '../KYC/KYCStatusBadge';
import Button from '../UI/Button';
import FormSection from '../UI/FormSection';

const KYCSection: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { status, expired, expirationDate, requiresKYC, isLoading } = useAppSelector((state) => state.kyc);

  useEffect(() => {
    dispatch(fetchKYCStatus());
  }, [dispatch]);

  // Calculate days until expiration
  const daysUntilExpiration = expirationDate && !expired
    ? Math.ceil((new Date(expirationDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : null;

  const isExpiringSoon = daysUntilExpiration !== null && daysUntilExpiration <= 30 && daysUntilExpiration > 0;

  if (isLoading) {
    return (
      <FormSection title="KYC Verification">
        <p className="text-sm text-[var(--color-subtext-color)]">Loading KYC status...</p>
      </FormSection>
    );
  }

  return (
    <FormSection
      title="KYC Verification"
      description="Know Your Customer verification is required to become a verified citizen and unlock full platform features."
    >
      <div className="space-y-4">
        {/* Status Display */}
        <div className="flex items-center justify-between p-4 bg-[var(--color-neutral-50)] border border-[var(--color-neutral-border)] rounded-lg">
          <div className="flex items-center gap-3">
            <KYCStatusBadge status={status} citizenStatus={user?.kyc?.citizenStatus} />
            {user?.kyc?.submittedAt && (
              <span className="text-sm text-[var(--color-subtext-color)]">
                Submitted: {new Date(user.kyc.submittedAt).toLocaleDateString()}
              </span>
            )}
            {user?.kyc?.approvedAt && (
              <span className="text-sm text-[var(--color-subtext-color)]">
                Approved: {new Date(user.kyc.approvedAt).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>

        {/* Expiration Info */}
        {expirationDate && (
          <div className={`
            p-4 rounded-lg border
            ${expired
              ? 'bg-[var(--color-error-50)] border-[var(--color-error-200)]'
              : isExpiringSoon
              ? 'bg-[var(--color-warning-50)] border-[var(--color-warning-200)]'
              : 'bg-[var(--color-success-50)] border-[var(--color-success-200)]'
            }
          `}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`
                  text-sm font-medium mb-1
                  ${expired
                    ? 'text-[var(--color-error-700)]'
                    : isExpiringSoon
                    ? 'text-[var(--color-warning-700)]'
                    : 'text-[var(--color-success-700)]'
                  }
                `}>
                  {expired
                    ? 'KYC Expired'
                    : isExpiringSoon
                    ? `Expiring in ${daysUntilExpiration} day${daysUntilExpiration !== 1 ? 's' : ''}`
                    : 'KYC Active'}
                </p>
                <p className="text-xs text-[var(--color-subtext-color)]">
                  Expiration Date: {new Date(expirationDate).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Status Messages */}
        {status === 'approved' && !expired && (
          <div className="p-4 bg-[var(--color-success-50)] border border-[var(--color-success-200)] rounded-lg">
            <p className="text-sm text-[var(--color-success-700)]">
              ✓ Your KYC has been approved! You are a verified citizen.
            </p>
          </div>
        )}

        {status === 'pending' && (
          <div className="p-4 bg-[var(--color-warning-50)] border border-[var(--color-warning-200)] rounded-lg">
            <p className="text-sm text-[var(--color-warning-700)]">
              Your KYC submission is under review. You will be notified once the review is complete.
            </p>
          </div>
        )}

        {status === 'rejected' && user?.kyc?.verificationData?.rejectionReason && (
          <div className="p-4 bg-[var(--color-error-50)] border border-[var(--color-error-200)] rounded-lg">
            <h4 className="font-semibold text-[var(--color-error-800)] mb-2">Rejection Reason</h4>
            <p className="text-sm text-[var(--color-error-700)]">{user.kyc.verificationData.rejectionReason}</p>
          </div>
        )}

        {expired && (
          <div className="p-4 bg-[var(--color-error-50)] border border-[var(--color-error-200)] rounded-lg">
            <p className="text-sm text-[var(--color-error-700)] mb-2">
              Your KYC verification has expired. Please complete the verification process again.
            </p>
          </div>
        )}

        {/* Action Button */}
        <div className="pt-4 border-t border-[var(--color-neutral-border)]">
          <Link to="/kyc">
            <Button
              variant={expired || requiresKYC || status === 'none' ? 'primary' : 'outline'}
              className="w-full md:w-auto"
            >
              {expired || requiresKYC || status === 'none'
                ? 'Start KYC Verification'
                : status === 'pending'
                ? 'View KYC Status'
                : status === 'rejected'
                ? 'Resubmit KYC'
                : 'Update KYC'}
            </Button>
          </Link>
        </div>
      </div>
    </FormSection>
  );
};

export default KYCSection;

